import { getCookie, setCookie } from 'cookies-next';

export type LpProductListCols = 2 | 3;
export type LpProductListItemUiPage =
  | 'ally'
  | 'best'
  | 'category'
  | 'category2'
  | 'exclusive'
  | 'issue'
  | 'new'
  | 'promotion'
  | 'sale';

interface LpProductListItemUiButtonClickParams {
  readonly area: `${LpProductListItemUiPage}_itemui_button`;
  readonly text: `${LpProductListCols}열`;
}

export const DEFAULT_LP_PRODUCT_LIST_COLS: LpProductListCols = 2;
export const WCK_LP_PRODUCT_LIST_COLS_COOKIE_KEY = 'wckLpProductListCols';

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 10000;
const COOKIE_DOMAIN = '.wconcept.co.kr';

/**
 * LP 페이지뷰에 전송할 현재 상품 열 수 문구를 생성합니다.
 *
 * @param cols 현재 상품 열 수
 * @returns page_view 이벤트의 itemList 파라미터
 * @example getLpProductListItemListParam(3)
 */
export function getLpProductListItemListParam(
  cols: LpProductListCols
): `${LpProductListCols}열` {
  return `${cols}열`;
}

/**
 * LP 열 전환 버튼의 GA4 클릭 영역과 현재 열 수 문구를 생성합니다.
 *
 * @param page GA4 페이지 식별자
 * @param cols 클릭 후 적용할 상품 열 수
 * @returns button_click 이벤트의 area와 text 파라미터
 * @example getLpProductListItemUiButtonClickParams('best', 3)
 */
export function getLpProductListItemUiButtonClickParams(
  page: LpProductListItemUiPage,
  cols: LpProductListCols
): LpProductListItemUiButtonClickParams {
  return {
    area: `${page}_itemui_button`,
    text: getLpProductListItemListParam(cols),
  };
}

/**
 * 현재 LP 상품 열 수에 맞는 API 위치 값을 선택합니다.
 *
 * @param twoColumnPosition 기존 2열 위치 값
 * @param threeColumnPosition 3열 전용 위치 값
 * @param cols 현재 상품 열 수
 * @returns 현재 열 수에 맞는 위치 값
 * @example getLpProductListColumnPosition(8, 12, 3)
 */
export function getLpProductListColumnPosition(
  twoColumnPosition: number | undefined,
  threeColumnPosition: number | undefined,
  cols: LpProductListCols
): number | undefined {
  return cols === 3 ? threeColumnPosition : twoColumnPosition;
}

export function parseLpProductListCols(value: unknown): LpProductListCols {
  if (value === 2 || value === '2') {
    return 2;
  }
  if (value === 3 || value === '3') {
    return 3;
  }
  return DEFAULT_LP_PRODUCT_LIST_COLS;
}

export function getLpProductListCols(): LpProductListCols {
  if (typeof window === 'undefined') {
    return DEFAULT_LP_PRODUCT_LIST_COLS;
  }

  return parseLpProductListCols(getCookie(WCK_LP_PRODUCT_LIST_COLS_COOKIE_KEY));
}

function getCookieDomain(): string | undefined {
  if (window.location.hostname.endsWith('wconcept.co.kr')) {
    return COOKIE_DOMAIN;
  }
  return undefined;
}

export function setLpProductListCols(cols: LpProductListCols): void {
  if (typeof window === 'undefined') {
    return;
  }

  setCookie(WCK_LP_PRODUCT_LIST_COLS_COOKIE_KEY, String(cols), {
    path: '/',
    domain: getCookieDomain(),
    maxAge: COOKIE_MAX_AGE_SECONDS,
  });
}
